/* import __COLOCATED_TEMPLATE__ from './team-details.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { trackedInLocalStorage } from 'ember-tracked-local-storage';
import { action } from '@ember/object';
import { useFunction } from 'ember-resources';
import { inject as service } from '@ember/service';
import type Session from 'embercom/services/session';
import TeamSummary from 'embercom/objects/inbox/team-summary';
import type AdminSummary from 'embercom/objects/inbox/admin-summary';
import type Inbox from 'embercom/objects/inbox/inboxes/inbox';
import type InboxState from 'embercom/services/inbox-state';
import { request } from 'embercom/lib/inbox/requests';
import type IntlService from 'embercom/services/intl';
import cached from 'embercom/lib/cached-decorator';

interface Args {
  inbox: Inbox;
  // If the inbox is being rendered as part of a custom folder this argument will have the
  // id of the custom folder
  customFolderId?: number;
}

interface Signature {
  Args: Args;
}

const SELECTED_COUNT_TYPE_KEY = 'inbox2::team-details::selected-count-type';

type CountType = 'all' | 'conversations' | 'tickets';

export default class UnnamedComponent extends Component<Signature> {
  @service declare session: Session;
  @service declare inboxState: InboxState;
  @service declare intl: IntlService;

  @tracked isLoadingTeam = false;
  @tracked hasError = false;
  @trackedInLocalStorage({ keyName: SELECTED_COUNT_TYPE_KEY, defaultValue: 'all' })
  selectedCountType: CountType = 'all';

  get canSeeSeparatedCounts(): boolean {
    return true;
  }

  get team(): TeamSummary {
    return this.teamAndSettingsLoader.value?.team ?? {};
  }

  get allAdmins(): AdminSummary[] {
    if (!this.team.adminSummaries) {
      return [];
    }
    return this.team.adminSummaries;
  }

  get onlineAdmins(): AdminSummary[] {
    return this.allAdmins.filter((admin) => !admin.isAway).sort(this.adminSortOrder);
  }

  get offlineAdmins(): AdminSummary[] {
    return this.allAdmins.filter((admin) => admin.isAway).sort(this.adminSortOrder);
  }

  adminSortOrder(a: AdminSummary, b: AdminSummary) {
    // openCount desc, name asc
    return b.openCount! - a.openCount! || a.name.localeCompare(b.name);
  }

  get capacityLimit(): number {
    return this.teamAndSettingsLoader.value?.capacityLimit ?? 0;
  }

  adminConversationLimit(admin: AdminSummary): number {
    return admin.workloadManagementSettings?.assignmentLimit || this.capacityLimit;
  }

  get ticketAssignmentLimit(): number {
    return this.teamAndSettingsLoader.value?.ticketAssignmentLimit ?? 0;
  }

  adminTicketLimit(admin: AdminSummary): number {
    return admin.workloadManagementSettings?.ticketAssignmentLimit || this.ticketAssignmentLimit;
  }

  get ticketAssignmentLimitEnabled(): boolean {
    return this.teamAndSettingsLoader.value?.ticketAssignmentLimitEnabled ?? false;
  }

  adminConversationsCount(admin: AdminSummary): number {
    return admin.countMetadata?.conversations ?? 0;
  }

  adminTicketsCount(admin: AdminSummary): number {
    return admin.countMetadata?.tickets ?? 0;
  }

  get lbaDescription() {
    return 'inbox.inbox-list.balanced-description-updated';
  }

  get ticketsAssignmentDisabled(): boolean {
    if (!this.canSeeSeparatedCounts) {
      return false;
    }
    return this.teamAndSettingsLoader.value?.ticketsAssignmentDisabled ?? false;
  }

  get isBlocking(): boolean {
    if (!this.adminsBlockable) {
      return false;
    }

    if (this.session.workspace.isFeatureEnabled('team-support-retain-role-level-capacity')) {
      return false;
    }

    return (
      (this.isEveryOnlineAdminAtCapacity && this.team.teamSettings?.volumeControlEnabled) || false
    );
  }

  get adminsBlockable(): boolean {
    if (!this.team.teamSettings) {
      return false;
    }
    return this.team.teamSettings.assignmentDistribution === 'load_balanced';
  }

  get adminIsMember(): boolean {
    if (!this.allAdmins) {
      return false;
    }

    return this.allAdmins.some((admin) => admin.id === this.session.teammate.id);
  }

  private get isEveryOnlineAdminAtCapacity(): boolean {
    return this.onlineAdmins.length === this.adminsAtCapacity.length;
  }

  get adminsAtCapacity(): AdminSummary[] {
    if (!this.adminsBlockable) {
      return [];
    }

    return this.allAdmins.filter((admin: AdminSummary) => {
      if (this.separateCapacityLimitEnabled) {
        let atConversationLimit =
          this.adminConversationsCount(admin) >= this.adminConversationLimit(admin);
        let atTicketLimit = this.adminTicketsCount(admin) >= this.adminTicketLimit(admin);

        return atConversationLimit && atTicketLimit;
      } else {
        let atAssignmentLimit = admin.openCount! >= this.adminConversationLimit(admin);

        return atAssignmentLimit;
      }
    });
  }

  get adminsAtConversationCapacity(): AdminSummary[] {
    if (!this.adminsBlockable) {
      return [];
    }

    return this.allAdmins.filter((admin: AdminSummary) => {
      return this.adminConversationsCount(admin) >= this.adminConversationLimit(admin);
    });
  }

  get adminsAtTicketCapacity(): AdminSummary[] {
    if (!this.adminsBlockable) {
      return [];
    }

    return this.allAdmins.filter((admin: AdminSummary) => {
      return this.adminTicketsCount(admin) >= this.adminTicketLimit(admin);
    });
  }

  get selectedCounts(): number[] {
    return this.allAdmins.map((admin) => {
      switch (this.selectedCountType) {
        case 'tickets':
          return this.adminTicketsCount(admin);
        case 'conversations':
          return this.adminConversationsCount(admin);
        default:
          return admin.openCount || 0;
      }
    });
  }

  get totalSelectedCount(): number {
    return this.selectedCounts.reduce((acc, val) => acc + val, 0);
  }

  get totalTeamAssignmentCount(): number {
    // Only show team assignment count on ALL tab and when feature flag is enabled
    if (
      this.selectedCountType === 'all' &&
      this.session.workspace.isFeatureEnabled('spg-assignment-limits-team-settings')
    ) {
      return this.allAdmins.reduce((acc, admin) => {
        let teamCount = admin.getTeamAssignmentCount(this.team.id) || 0;
        return acc + teamCount;
      }, 0);
    }

    // For conversations and tickets tabs, or when FF is disabled, return 0
    return 0;
  }

  get separateCapacityLimitEnabled(): boolean {
    return !this.ticketsAssignmentDisabled && this.ticketAssignmentLimitEnabled;
  }

  get shouldShowTeammatesAtCapacity(): boolean {
    if (!this.adminsBlockable) {
      return false;
    }

    // Always show if there are team capacity issues
    if (this.adminsAtTeamCapacity.length > 0) {
      return true;
    }

    if (this.separateCapacityLimitEnabled) {
      return this.shouldShowCapacityForSeparateLimits();
    }

    return this.shouldShowCapacityForLegacyMode();
  }

  private shouldShowCapacityForSeparateLimits(): boolean {
    let capacityChecks: Record<CountType, boolean> = {
      all: this.adminsAtCapacity.length > 0,
      conversations: this.adminsAtConversationCapacity.length > 0,
      tickets: this.adminsAtTicketCapacity.length > 0,
    };

    return capacityChecks[this.selectedCountType] || false;
  }

  private shouldShowCapacityForLegacyMode(): boolean {
    let hasCapacityIssues = this.adminsAtCapacity.length > 0 || this.isBlocking;
    if (!hasCapacityIssues) {
      return false;
    }

    if (this.selectedCountType === 'all') {
      return !this.ticketsAssignmentDisabled;
    }

    if (this.selectedCountType === 'conversations') {
      return this.ticketsAssignmentDisabled;
    }

    return false;
  }

  get teammatesAtCapacityText(): string {
    if (this.isBlocking) {
      return this.intl.t('inbox.inbox-list.inbox-blocking');
    }

    let adminsAtAnyCapacity = this.adminsAtAnyCapacity;
    if (adminsAtAnyCapacity.length === 0) {
      return '';
    }

    return this.intl.t('inbox.inbox-list.blocked-teammates-count', {
      count: adminsAtAnyCapacity.length,
    });
  }

  get adminsAtAnyCapacity(): AdminSummary[] {
    // Get unique teammates who are at either team capacity OR individual capacity
    let teamCapacityAdmins = new Set(this.adminsAtTeamCapacity.map((admin) => admin.id));

    let individualCapacityAdmins = new Set<number>();

    // Get individual capacity admins based on current mode
    if (this.separateCapacityLimitEnabled) {
      let capacityMap: Record<CountType, AdminSummary[]> = {
        all: this.adminsAtCapacity,
        conversations: this.adminsAtConversationCapacity,
        tickets: this.adminsAtTicketCapacity,
      };

      let adminsAtCapacity = capacityMap[this.selectedCountType] || [];
      individualCapacityAdmins = new Set(adminsAtCapacity.map((admin) => admin.id));
    } else {
      individualCapacityAdmins = new Set(this.adminsAtCapacity.map((admin) => admin.id));
    }

    // Combine both sets to get unique admin IDs
    let allCapacityAdminIds = new Set([...teamCapacityAdmins, ...individualCapacityAdmins]);

    // Return the actual admin objects for the unique IDs
    return this.allAdmins.filter((admin) => allCapacityAdminIds.has(admin.id));
  }

  get adminsAtTeamCapacity(): AdminSummary[] {
    // Only check team capacity if feature flag is enabled
    if (!this.session.workspace.isFeatureEnabled('spg-assignment-limits-team-settings')) {
      return [];
    }

    // Only show team capacity violations on the ALL tab since team limits
    if (this.selectedCountType !== 'all') {
      return [];
    }

    let teamLimit = this.team.teamSettings?.assignmentLimit;
    if (!teamLimit) {
      return [];
    }

    return this.allAdmins.filter((admin) => {
      let teamCount = admin.getTeamAssignmentCount(this.team.id);
      return teamCount >= teamLimit;
    });
  }

  @action
  onCountTypeChange(countType: CountType) {
    this.selectedCountType = countType;
  }

  @cached({ max: 1, ttl: 1000 * 60 })
  private async fetchCapacityLimits() {
    let response = await request(
      `/ember/app_conversation_capacity_limits/${this.session.workspace.id}?app_id=${this.session.workspace.id}`,
    );
    return await response.json();
  }

  @cached({ max: 100, ttl: 1000 * 60 })
  private async fetchTeamDetails(id: string) {
    let response = await request(`/ember/inbox/teams/${id}?app_id=${this.session.workspace.id}`);
    return await response.json();
  }

  private teamAndSettingsLoader = useFunction(
    this,
    async (previous: any, id: string): Promise<any> => {
      if (previous && id === previous.id) {
        return previous;
      }

      try {
        this.hasError = false;
        this.isLoadingTeam = true;
        let [
          { team },
          {
            capacity_limit,
            tickets_assignment_disabled,
            ticket_assignment_limit,
            ticket_assignment_limit_enabled,
          },
        ] = await Promise.all([this.fetchTeamDetails(id), this.fetchCapacityLimits()]);
        return {
          id,
          team: TeamSummary.deserialize(team),
          capacityLimit: capacity_limit,
          ticketsAssignmentDisabled: tickets_assignment_disabled,
          ticketAssignmentLimit: ticket_assignment_limit,
          ticketAssignmentLimitEnabled: ticket_assignment_limit_enabled,
        };
      } catch (err) {
        this.hasError = true;
      } finally {
        this.isLoadingTeam = false;
      }
    },
    () => [this.args.inbox.id],
  );
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::TeamDetails': typeof UnnamedComponent;
    'inbox2/team-details': typeof UnnamedComponent;
  }
}
